import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2, Dumbbell, ChevronRight, Sparkles, Play, CheckCircle2, History, ListChecks } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { PageLoader } from '../../components/PageLoader'
import { Skeleton } from '../../components/ui/Skeleton'
import PullToRefresh from '../../components/PullToRefresh'
import { useAuth } from '../../contexts/AuthContext'
import { useIsMobile } from '../../hooks/useIsMobile'
import { useExercises } from '../../hooks/useExercises'
import { seedDefaults } from '../../lib/workoutSeed'
import { triggerUpdate } from '../../serviceWorkerRegistration'
import ExerciseLibrary from './ExerciseLibrary'
import ExerciseDetail from './ExerciseDetail'
import NewSession from './NewSession'
import SessionHistory from './SessionHistory'
import SessionDetail from './SessionDetail'
import TemplatesEditor from './TemplatesEditor'
import WorkoutCoach from './components/WorkoutCoach'
import { LoginPage } from '../phoenix/LoginPage'

export default function WorkoutShell() {
  const { user, loading: authLoading } = useAuth()

  if (authLoading) return <PageLoader />

  if (!user) return <LoginPage />

  return (
    <Routes>
      <Route index element={<WorkoutDashboard />} />
      <Route path="new" element={<NewSession />} />
      <Route path="history" element={<SessionHistory />} />
      <Route path="sessions/:id" element={<SessionDetail />} />
      <Route path="exercises" element={<ExerciseLibrary />} />
      <Route path="exercises/:id" element={<ExerciseDetail />} />
      <Route path="templates" element={<TemplatesEditor />} />
      <Route path="*" element={<Navigate to="/workout" replace />} />
    </Routes>
  )
}

function WorkoutDashboard() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { exercises, loading, refetch } = useExercises()
  const location = useLocation()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [seeding, setSeeding] = useState(false)
  const [seedError, setSeedError] = useState(null)
  const [toast, setToast] = useState(location.state?.toast || null)

  // Mirrors the Phoenix refresh handler — wait on the actual refetch
  // and floor the spinner at one full rotation (1s) so a cache hit
  // doesn't cut the loader off mid-spin.
  const handleRefresh = useCallback(async () => {
    triggerUpdate()
    await Promise.all([
      Promise.resolve(refetch()),
      new Promise((resolve) => setTimeout(resolve, 1000)),
    ])
  }, [refetch])

  // Bottom-nav tab selection. 'workouts' is the current page; the rest
  // route back to /phoenix with the desired tab forwarded via location
  // state so PhoenixTracker can open straight onto it.
  const handleSelectTab = useCallback(
    (key) => {
      if (key === 'workouts') return
      navigate('/', { state: { tab: key } })
    },
    [navigate]
  )

  // Auto-dismiss the success toast forwarded from /workout/new after save.
  useEffect(() => {
    if (!toast) return undefined
    const id = setTimeout(() => setToast(null), 3000)
    return () => clearTimeout(id)
  }, [toast])

  // Sync new DEFAULT_EXERCISES added to the seed since the user signed up.
  // seedDefaults is row-level idempotent so this is cheap when there's
  // nothing to add (one SELECT). Runs once per user-id per mount.
  const syncedForUserRef = useRef(null)
  useEffect(() => {
    if (!user?.id || syncedForUserRef.current === user.id) return
    syncedForUserRef.current = user.id
    let cancelled = false
    ;(async () => {
      const res = await seedDefaults(user.id)
      if (cancelled) return
      if (res.ok && res.count > 0) refetch()
    })()
    return () => {
      cancelled = true
    }
  }, [user?.id, refetch])

  const handleSeed = async () => {
    if (!user) return
    setSeeding(true)
    setSeedError(null)
    const res = await seedDefaults(user.id)
    setSeeding(false)
    if (!res.ok) {
      setSeedError(res.reason)
      return
    }
    await refetch()
  }

  if (loading) {
    return (
      <div className="space-y-6 sm:space-y-8 content-fade">
        <Skeleton className="h-14 w-2/3" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    )
  }

  const isEmpty = exercises.length === 0

  return (
    <>
    <PullToRefresh onRefresh={handleRefresh}>
    <div className="space-y-6 sm:space-y-8 content-fade">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="rounded-full bg-app-primary-100 dark:bg-app-primary-900/30 p-3 flex-shrink-0">
          <Dumbbell className="w-6 h-6 text-app-primary-500" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
            {t('workout.title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('workout.subtitle')}
          </p>
        </div>
      </div>

      {/* Empty state — first run */}
      {isEmpty && (
        <Card className="border-app-primary-500/30">
          <CardContent className="p-6 text-center">
            <Sparkles className="w-10 h-10 text-app-primary-500 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-foreground mb-1">
              {t('workout.dashboard.firstRunTitle')}
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              {t('workout.dashboard.firstRunDesc')}
            </p>
            <Button
              onClick={handleSeed}
              disabled={seeding}
              className="bg-app-primary-500 hover:bg-app-primary-600 text-white"
            >
              {seeding ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin me-1.5" />
                  {t('workout.dashboard.seeding')}
                </>
              ) : (
                t('workout.dashboard.setupCta')
              )}
            </Button>
            {seedError && (
              <p className="text-xs text-red-500 mt-2">{seedError}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Success toast from save flow */}
      {toast && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
          <p className="text-sm text-green-700 dark:text-green-300">{toast}</p>
        </div>
      )}

      {/* Primary CTA + library quick-link — when not empty */}
      {!isEmpty && (
        <>
          <WorkoutCoach />

          <Link
            to="/workout/new"
            className="block rounded-lg bg-app-primary-500 hover:bg-app-primary-600 text-white p-4 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-white/15 p-2">
                <Play className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">
                  {t('workout.dashboard.startCard')}
                </p>
                <p className="text-xs text-white/80">
                  {t('workout.dashboard.startCardDesc')}
                </p>
              </div>
              <ChevronRight className="w-4 h-4 rtl:-scale-x-100" />
            </div>
          </Link>

          <Link
            to="/workout/history"
            className="block rounded-lg border border-border bg-card p-4 hover:border-app-primary-500/40 hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-app-primary-100 dark:bg-app-primary-900/30 p-2">
                <History className="w-4 h-4 text-app-primary-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {t('workout.dashboard.historyCard')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('workout.dashboard.historyCardDesc')}
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground rtl:-scale-x-100" />
            </div>
          </Link>

          <Link
            to="/workout/exercises"
            className="block rounded-lg border border-border bg-card p-4 hover:border-app-primary-500/40 hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-app-primary-100 dark:bg-app-primary-900/30 p-2">
                <Dumbbell className="w-4 h-4 text-app-primary-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {t('workout.dashboard.libraryCard')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('workout.dashboard.libraryCardDesc', { count: exercises.length })}
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground rtl:-scale-x-100" />
            </div>
          </Link>

          <Link
            to="/workout/templates"
            className="block rounded-lg border border-border bg-card p-4 hover:border-app-primary-500/40 hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-app-primary-100 dark:bg-app-primary-900/30 p-2">
                <ListChecks className="w-4 h-4 text-app-primary-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {t('workout.dashboard.templatesCard')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('workout.dashboard.templatesCardDesc')}
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground rtl:-scale-x-100" />
            </div>
          </Link>
        </>
      )}
    </div>
    </PullToRefresh>
    {/* BottomTabBar is rendered at the App level (hoisted) so it
        doesn't remount when the user jumps between /workout and
        /phoenix sub-tabs. */}
    </>
  )
}
